/**
 * Self-Contract 节点视图组件
 * 用于渲染 Lexical SelfContractNode（只读视图）
 */

import { AlertCircle, Calendar, ChevronDown, ChevronUp, Clock, FileText, Target, TrendingUp } from "lucide-react";
import React, { useState } from "react";
import type { SerializedSelfContractNode } from "@/components/Editor/nodes/types";
import { CONTRACT_STATUS_LABELS, CONTRACT_TEMPLATE_LABELS } from "@/components/Editor/nodes/types";
import { DecisionsList } from "@/components/SelfContract/DecisionsList";
import { cn } from "@/lib/utils";
import { useLocale } from "@/utils/i18n";
import { Ticker } from "./Ticker";

interface SelfContractViewProps {
  node: SerializedSelfContractNode;
  className?: string;
  contractUid?: string; // Optional: contract ID for querying related decisions
}

const statusColors: Record<string, string> = {
  active: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 border-green-300 dark:border-green-700",
  triggered: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 border-blue-300 dark:border-blue-700",
  cancelled: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 border-gray-300 dark:border-gray-600",
  expired: "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300 border-orange-300 dark:border-orange-700",
};

const templateIcons: Record<string, React.ReactNode> = {
  "value-oriented": <TrendingUp className="w-4 h-4" />,
  "trading-oriented": <Clock className="w-4 h-4" />,
};

export const SelfContractView: React.FC<SelfContractViewProps> = ({ node, className, contractUid }) => {
  const locale = useLocale();
  const { ticker, template, expiration, idea, trigger, action, status } = node;

  // State for collapsible decisions section
  const [showDecisions, setShowDecisions] = useState(false);

  // 格式化日期
  const formatDate = (dateStr: string, locale: string) => {
    if (!dateStr) return "未设置";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString(locale, { year: "numeric", month: "2-digit", day: "2-digit" });
    } catch {
      return dateStr;
    }
  };

  // 获取触发条件描述
  const getTriggerSummary = () => {
    // 防御性检查：trigger 可能不存在
    if (!trigger || !trigger.type) {
      return "未设置触发条件";
    }

    switch (trigger.type) {
      case "price": {
        const op = trigger.operator || "==";
        const price = trigger.priceLevel;
        return price !== undefined ? `价格 ${op} ${price.toFixed(2)}` : "价格触发";
      }
      case "expiration":
        return `到期: ${formatDate(expiration, locale)}`;
      case "time":
        return trigger.notes || "时间触发";
      case "custom":
        return trigger.notes || "自定义触发";
      default:
        return "未设置";
    }
  };

  const isExpiredOrCancelled = status === "expired" || status === "cancelled";
  const isTriggered = status === "triggered";
  const isActive = status === "active";
  const showDecisionsSection = contractUid && !isExpiredOrCancelled;

  return (
    <div
      className={cn(
        "my-4 rounded-lg border bg-card shadow-sm overflow-hidden",
        "self-contract-node",
        isExpiredOrCancelled ? "opacity-70" : "",
        isActive ? "ring-1 ring-primary/20" : "",
        isTriggered ? "ring-2 ring-blue-300 dark:ring-blue-700" : "",
        className,
      )}
    >
      {/* Header: Ticker + Template + Status */}
      <div className={cn("flex items-center justify-between px-4 py-3 border-b", isActive ? "bg-primary/5" : "bg-muted/30")}>
        <div className="flex items-center gap-3">
          {/* Ticker */}
          <div className="flex items-center gap-1.5">
            <Target className="w-4 h-4 text-primary" />
            <Ticker data-ticker={ticker} variant="lexical" showDollarSign={false} className="font-bold text-base">
              ${ticker}
            </Ticker>
          </div>
          <div className="h-4 w-px bg-border" />
          {/* Template */}
          <div
            className={cn(
              "flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-white dark:bg-gray-800 border",
              template === "value-oriented"
                ? "text-green-600 dark:text-green-400 border-green-200 dark:border-green-800"
                : "text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800",
            )}
          >
            {templateIcons[template]}
            <span>{CONTRACT_TEMPLATE_LABELS[template] || template}</span>
          </div>
        </div>

        {/* Status */}
        <span className={cn("px-2.5 py-1 text-xs font-medium rounded-full border", statusColors[status])}>
          {CONTRACT_STATUS_LABELS[status] || status}
        </span>
      </div>

      {/* Body */}
      <div className="p-4 space-y-3">
        {/* Expiration + Trigger */}
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Calendar className="w-3.5 h-3.5" />
            <span>到期: {formatDate(expiration, locale)}</span>
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <AlertCircle className="w-3.5 h-3.5" />
            <span>{getTriggerSummary()}</span>
          </div>
        </div>

        {/* Idea */}
        <div className="bg-muted/50 rounded-md p-3">
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1.5">
            <FileText className="w-3 h-3" />
            <span>投资观点</span>
          </div>
          <p className="text-sm leading-relaxed">{idea || "未描述"}</p>
        </div>

        {/* Action */}
        {action && (
          <div className="pl-3 border-l-2 border-primary/50">
            <div className="text-xs font-medium text-muted-foreground mb-1">触发后行动:</div>
            <p className="text-sm leading-relaxed">{action}</p>
          </div>
        )}
      </div>

      {/* Decisions Section (Collapsible) */}
      {showDecisionsSection && (
        <div className="border-t">
          <button
            type="button"
            onClick={() => setShowDecisions(!showDecisions)}
            className="w-full flex items-center justify-between px-4 py-3 bg-muted/20 hover:bg-muted/30 transition-colors"
          >
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">关联决策</span>
            </div>
            {showDecisions ? (
              <ChevronUp className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            )}
          </button>
          {showDecisions && (
            <div className="p-4 max-h-96 overflow-y-auto">
              <DecisionsList contractUid={contractUid} ticker={ticker} limit={5} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SelfContractView;
